import { FC, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useHubStationsForPair } from "@ryde/redux";
import {
  BookingFormFields,
  DirectionEnum,
  ICustomField,
  isValidPositiveNumber,
} from "@ryde/shared";

import { HubStationTemplate } from "./hub-station.template";
import { HubStationsProvider } from "./hub-stations.context";

const DEFAULT_ORIGIN_FIELD = "originStationId";
const DEFAULT_DESTINATION_FIELD = "destinationStationId";
const DEFAULT_HUB_FIELD = "hubId";
const DEFAULT_DIRECTION_FIELD = BookingFormFields.direction;

export const HubStationStandaloneTemplate: FC<ICustomField> = ({
  name,
  formAttributes,
}) => {
  const { t } = useTranslation();
  const { watch, setError, clearErrors } = useFormContext();
  const originField = formAttributes?.originFieldName ?? DEFAULT_ORIGIN_FIELD;
  const destinationField =
    formAttributes?.destinationFieldName ?? DEFAULT_DESTINATION_FIELD;
  const directionField =
    formAttributes?.directionFieldName ?? DEFAULT_DIRECTION_FIELD;
  const hubField = name ?? DEFAULT_HUB_FIELD;

  const originStationId = watch(originField) as number | null | undefined;
  const destinationStationId = watch(destinationField) as
    number | null | undefined;
  const currentHubId = watch(hubField) as number | null | undefined;
  const direction = watch(directionField) as DirectionEnum | undefined;

  const hubStations = useHubStationsForPair({
    originStationId,
    destinationStationId,
    currentHubId,
    direction,
  });

  useEffect(() => {
    const isSameAsBookingStation =
      isValidPositiveNumber(currentHubId) &&
      ((isValidPositiveNumber(originStationId) &&
        currentHubId === originStationId) ||
        (isValidPositiveNumber(destinationStationId) &&
          currentHubId === destinationStationId));

    if (isSameAsBookingStation) {
      setError(hubField, {
        type: "validate",
        message: t("hub.hubSameAsBookingStation"),
      });
      return;
    }

    clearErrors(hubField);
  }, [
    clearErrors,
    currentHubId,
    destinationStationId,
    hubField,
    originStationId,
    setError,
    t,
  ]);

  return (
    <HubStationsProvider value={hubStations}>
      <HubStationTemplate name={hubField} formAttributes={formAttributes} />
    </HubStationsProvider>
  );
};
